import React, { useEffect, useMemo, useState } from 'react';
import helpdeskService from '../services/helpdeskService';
import { useHelpdeskUdaScope } from '../components/HelpdeskUdaScope';
import { useScreenshotStore } from '../../../../stores/screenshotStore';
import { useToast } from '../../../../hooks/useToast';
import ToastContainer from '../../../../components/feature/ToastContainer';

// Strip the origin (http://localhost:3000, https://host, …) so the Path captures only
// the app route, e.g. "/app/utility/ds-helpdesk/tickets". Leaves non-URL text untouched.
const toRoutePath = (u?: string): string => {
  if (!u) return '';
  try { const url = new URL(u); return url.pathname + url.search + url.hash; }
  catch { return u; }
};

// Format a Date as the local "YYYY-MM-DDTHH:mm" that a <input type="datetime-local"> expects.
const toLocalDatetime = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

/**
 * Help Desk "Report Issue" / New Ticket form.
 *
 * Rendered inside the GlobalNewTicketModal overlay, which supplies the purple
 * header bar (back arrow, title, fullscreen + close). This component owns the
 * form body: the field grid, title/description, attachments and footer actions.
 *
 * Captured screenshots are read from the shared screenshotStore so they persist
 * across the round-trip from the floating Report Issue button. On submit the
 * screenshots (plus any manually attached files) are uploaded via
 * `helpdeskService.uploadAttachments`, and the returned attachment metadata is
 * attached to the ticket through the save payload's `attchmnt_jn` field.
 *
 * All helpdesk tables live in the `ds_dtcp` schema of the `dtcp_erp` database.
 * (The backend module key is `ds_helpdesk`, but no schema by that name exists.)
 *
 * Every dropdown reads the same master table its Setup page writes, so a row
 * added under Setup → Modules / Sub Modules / Sub Issue Types appears here.
 *
 *  - Component      ← getActiveApplications()            → hlpd_app_id            (hlpd_app_lst_t)
 *  - Module         ← getMenuModules()                   → mnu_itm_id             (app_mnu_itm_lst_t root items)
 *  - Sub Modules    ← getMenuSubModules()                → sub_mnu_itm_id         (app_mnu_itm_lst_t children, cascades off Module)
 *  - Category       ← getActiveCategories()              → hlpd_ctgry_id          (hlpd_ctgry_lst_t)
 *  - Issue Type     ← getActiveTicketTypes()             → hlpd_tckt_type_id      (hlpd_tckt_type_lst_t)
 *  - Sub Issue Type ← getSubIssueTypesByTicketType()     → hlpd_sub_issue_type_id (hlpd_sub_issue_type_lst_t, cascades off Issue Type)
 *  - Mode           ← getActiveModes()                   → cmpnt_id               (app_cmpnt_lst_t)
 *  - Priority is fetched and defaulted silently (kept out of the visible grid).
 *
 * Module is a menu item, NOT hlpd_cmpnt_id — this form leaves hlpd_cmpnt_id NULL
 * and stores the readable module name in cstm_flds_jn.module, which the ticket
 * queries COALESCE into `mdle_nm`.
 *
 * Component / Module / Category / Issue Type / Priority are stored in their own
 * ticket columns. Sub Module, Sub Issue Type, Mode and Occurred Time have no
 * column on hlpd_tckt_dtl_t, so they ride along inside the `cstm_flds_jn` jsonb blob.
 */
interface ReportIssuePageProps {
  /** Accepted for compatibility with GlobalNewTicketModal (which owns the modal
   *  chrome); this component always renders as the modal body. */
  isModal?: boolean;
  onClose?: () => void;
  onCreated?: () => void;
}

const ReportIssuePage: React.FC<ReportIssuePageProps> = ({ onClose, onCreated }) => {
  const { toasts, removeToast, success, error: toastError } = useToast();

  // Captured screenshots live in the store so they survive the round-trip from
  // the floating Report Issue button (which captures then opens this form).
  const screenshots = useScreenshotStore((s) => s.screenshots);
  const removeScreenshot = useScreenshotStore((s) => s.removeScreenshot);
  const clearScreenshots = useScreenshotStore((s) => s.clearScreenshots);
  const closeTicketModal = useScreenshotStore((s) => s.closeTicketModal);
  const bumpTicketsVersion = useScreenshotStore((s) => s.bumpTicketsVersion);

  // Close the modal: notify the host (onClose) AND flip the shared store flag.
  const closeModal = () => {
    onClose?.();
    closeTicketModal();
  };

  // The UDA the ticket is filed against (hlpd_tckt_dtl_t.uda_id). A ticket must belong
  // to one UDA, so the form asks for it rather than the helpdesk header having to be
  // on a single UDA first. It starts on the header's pick when there is one. It is
  // form-local on purpose: choosing a UDA here must not re-scope the ticket list behind.
  // A user tied to one UDA sees it as a fixed label; the server pins them regardless.
  const udaScope = useHelpdeskUdaScope();
  const [ticketUdaId, setTicketUdaId] = useState<string>(
    udaScope.udaId != null ? String(udaScope.udaId) : ''
  );
  useEffect(() => {
    if (!udaScope.canChoose && udaScope.udaId != null) setTicketUdaId(String(udaScope.udaId));
  }, [udaScope.canChoose, udaScope.udaId]);

  // Dropdown data (active IT Helpdesk masters)
  const [applications, setApplications] = useState<any[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [ticketTypes, setTicketTypes] = useState<any[]>([]);
  const [subModules, setSubModules] = useState<any[]>([]);
  // Sub Issue Type / Category fields are hidden on this form (commented out below).
  // const [subIssueTypes, setSubIssueTypes] = useState<any[]>([]);
  const [modes, setModes] = useState<any[]>([]);
  // const [categories, setCategories] = useState<any[]>([]);

  // Field state (maps to hlpd_* ticket columns)
  const [appId, setAppId] = useState<string>('');
  const [componentId, setComponentId] = useState<string>('');
  const [ticketTypeId, setTicketTypeId] = useState<string>('');
  const [priorityId, setPriorityId] = useState<string>('');
  // Category field hidden on this form (commented out below); model defaults hlpd_ctgry_id.
  // const [categoryId, setCategoryId] = useState<string>('');

  // No column on hlpd_tckt_dtl_t — these ride along inside cstm_flds_jn.
  const [subModule, setSubModule] = useState<string>('');
  // Sub Issue Type field hidden on this form (commented out below).
  // const [subIssueType, setSubIssueType] = useState<string>('');
  const [mode, setMode] = useState<string>('');
  const [occurredTime, setOccurredTime] = useState<string>('');
  // Reference Number (optional). Its label/placeholder adapt to the selected Module:
  // Property → Assessment Number, Water → Water HSC Code, else generic. Stored in
  // cstm_flds_jn.ref_no_tx (no dedicated column on hlpd_tckt_dtl_t).
  const [refNo, setRefNo] = useState<string>('');

  // Path = the page route where the issue occurred. Auto-filled from the first
  // screenshot's page URL (see effect below); the user can still edit or clear it.
  const [path, setPath] = useState<string>('');
  // Sub-Module id pending application: the auto-resolve sets Module first, which
  // triggers the sub-module list to (re)load; this holds the resolved sub-module id
  // until that list is in, then the apply effect selects it.
  const [pendingSubModuleId, setPendingSubModuleId] = useState<string>('');
  // True when the captured screenshots span more than one MODULE — one ticket may
  // only cover a single module, so submit is blocked while this is set.
  const [moduleConflict, setModuleConflict] = useState(false);

  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [manualFiles, setManualFiles] = useState<File[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reference Number label/placeholder, keyed off the selected Module's name.
  const selectedModuleName = useMemo(
    () => String(components.find((m: any) => String(m.mnu_itm_id) === componentId)?.mnu_itm_nm || '').toUpperCase().trim(),
    [components, componentId]
  );
  const refLabel = useMemo(() => {
    if (selectedModuleName.includes('PROPERTY')) return 'Assessment Number';
    if (selectedModuleName.includes('WATER')) return 'Water HSC Code';
    return 'Reference Number';
  }, [selectedModuleName]);
  const refPlaceholder = useMemo(() => {
    if (selectedModuleName.includes('PROPERTY')) return 'Enter property assessment number';
    if (selectedModuleName.includes('WATER')) return 'Enter water HSC code';
    return 'Enter reference number';
  }, [selectedModuleName]);

  // Load master data (applications, ticket types, priorities) on mount.
  useEffect(() => {
    (async () => {
      try {
        // Category field is hidden on this form, so getActiveCategories() is no longer loaded.
        const [appsRes, typesRes, priosRes, modesRes] = await Promise.all([
          helpdeskService.getActiveApplications(),
          helpdeskService.getActiveTicketTypes(),
          helpdeskService.getActivePriorities(),
          helpdeskService.getActiveModes(),
        ]);
        const apps: any[] = Array.isArray(appsRes.data) ? appsRes.data : [];
        setApplications(apps);
        setTicketTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
        setModes(Array.isArray(modesRes.data) ? modesRes.data : []);
        // setCategories(Array.isArray(ctgrysRes.data) ? ctgrysRes.data : []);

        // Default Component to the DTCP application so the screenshot → new-ticket flow
        // arrives with Component pre-filled (DTCP is the only active helpdesk app; match
        // by code/name, else fall back to the first app). Only set if the user hasn't
        // already picked one — set directly (not via the dropdown onChange) so it does
        // not clear an auto-resolved Module.
        const dtcpApp =
          apps.find((a: any) => String(a.hlpd_app_cd || '').trim().toUpperCase() === 'DTCP') ||
          apps.find((a: any) => String(a.hlpd_app_nm || '').toUpperCase().includes('DTCP')) ||
          apps[0];
        if (dtcpApp?.hlpd_app_id) setAppId((prev) => prev || String(dtcpApp.hlpd_app_id));

        // Default Priority silently (kept out of the visible grid) — flagged
        // default row when present, otherwise the first priority.
        const prios: any[] = Array.isArray(priosRes.data) ? priosRes.data : [];
        const defPrio = prios.find((p: any) => p.is_dflt_in) || prios[0];
        if (defPrio?.hlpd_prrty_id) setPriorityId((prev) => prev || String(defPrio.hlpd_prrty_id));
      } catch (e) {
        console.error('Failed to load helpdesk master data', e);
        toastError('Failed to load form options', 2500);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load Modules — root menu items scoped to the LOGGED-IN user's menu profile
  // (role/profile), i.e. the same modules shown in the user's sidebar, not the
  // whole app_mnu_itm_lst_t tree. Resolved from app_prfle_mnu_itm_rel_t via the
  // user's mnu_prfle_id (see erp-auth GET /menu/modules).
  useEffect(() => {
    (async () => {
      try {
        const res = await helpdeskService.getMyMenuModules();
        setComponents(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load modules', e);
        setComponents([]);
      }
    })();
  }, []);

  // Load Sub Modules — children of the chosen Module (prnt_mnu_itm_id = mnu_itm_id).
  useEffect(() => {
    setSubModule('');
    if (!componentId) {
      setSubModules([]);
      return;
    }
    (async () => {
      try {
        const res = await helpdeskService.getMenuSubModules(Number(componentId));
        setSubModules(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load sub modules', e);
        setSubModules([]);
      }
    })();
  }, [componentId]);

  // Sub Issue Type field is hidden on this form — loader disabled.
  // useEffect(() => {
  //   setSubIssueType('');
  //   if (!ticketTypeId) {
  //     setSubIssueTypes([]);
  //     return;
  //   }
  //   (async () => {
  //     try {
  //       const res = await helpdeskService.getSubIssueTypesByTicketType(Number(ticketTypeId));
  //       setSubIssueTypes(Array.isArray(res.data) ? res.data : []);
  //     } catch (e) {
  //       console.error('Failed to load sub issue types', e);
  //       setSubIssueTypes([]);
  //     }
  //   })();
  // }, [ticketTypeId]);

  // Auto-fill Path from the first screenshot's page URL (only while Path is empty, so
  // a manual edit / clear is respected).
  useEffect(() => {
    const firstUrl = screenshots.find((s) => s.pageUrl)?.pageUrl;
    if (firstUrl && !path) setPath(toRoutePath(firstUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshots]);

  // Auto-fill Occurred Time from the first screenshot's capture time (else now), while
  // it's still empty — so a manual edit is respected.
  useEffect(() => {
    if (occurredTime) return;
    const cap = screenshots.find((s) => s.capturedAt)?.capturedAt;
    const d = cap ? new Date(cap) : new Date();
    if (!isNaN(d.getTime())) setOccurredTime(toLocalDatetime(d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screenshots]);

  // Auto-resolve Module + Sub-Module from the captured Path (debounced). Sets the
  // Module dropdown (which cascades the Sub-Module list to reload) and stashes the
  // resolved sub-module id so the apply effect below can select it once the list is in.
  useEffect(() => {
    const p = toRoutePath(path.trim());
    if (!p) return;
    const h = setTimeout(async () => {
      try {
        const res = await helpdeskService.resolveMenuByPath(p);
        const d: any = (res?.data as any) ?? {};
        if (d.mdle_itm_id) {
          setComponentId(String(d.mdle_itm_id));
          setPendingSubModuleId(d.sub_mdle_itm_id ? String(d.sub_mdle_itm_id) : '');
        }
      } catch (e) {
        console.error('Menu resolve failed', e);
      }
    }, 400);
    return () => clearTimeout(h);
  }, [path]);

  // Apply the resolved Sub-Module once its options have loaded (the Module change
  // above reloads subModules and resets subModule to ''). Only applies if the resolved
  // id is actually one of the loaded children.
  useEffect(() => {
    if (!pendingSubModuleId) return;
    if (subModules.some((s: any) => String(s.mnu_itm_id) === pendingSubModuleId)) {
      setSubModule(pendingSubModuleId);
      setPendingSubModuleId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subModules, pendingSubModuleId]);

  // Live check: warn as soon as the screenshots resolve to more than one MODULE. One
  // ticket = one module (screenshots from different modules must be separate tickets).
  useEffect(() => {
    const distinctPaths = Array.from(new Set(screenshots.map((s) => toRoutePath(s.pageUrl)).filter(Boolean)));
    if (distinctPaths.length <= 1) { setModuleConflict(false); return; }
    let cancelled = false;
    const h = setTimeout(async () => {
      try {
        const resolved = await Promise.all(distinctPaths.map((p) => helpdeskService.resolveMenuByPath(p)));
        if (cancelled) return;
        const keys = new Set(
          resolved
            .map((r: any) => (r?.data as any)?.mdle_itm_id ?? null)
            .filter((v: any) => v != null)
            .map((v: any) => String(v))
        );
        setModuleConflict(keys.size > 1);
      } catch { /* the submit-time check is the hard gate */ }
    }, 500);
    return () => { cancelled = true; clearTimeout(h); };
  }, [screenshots]);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setManualFiles((prev) => [...prev, ...Array.from(e.target.files as FileList)]);
  };

  const removeManual = (i: number) => setManualFiles((prev) => prev.filter((_, idx) => idx !== i));

  // Convert each captured screenshot's data URL into a File for upload.
  const screenshotsToFiles = (): File[] =>
    screenshots.map((shot, i) => {
      const [, mime = 'image/png', b64 = ''] = /^data:(.*?);base64,(.*)$/.exec(shot.dataUrl) || [];
      const bytes = atob(b64);
      const arr = new Uint8Array(bytes.length);
      for (let j = 0; j < bytes.length; j++) arr[j] = bytes.charCodeAt(j);
      return new File([arr], `screenshot-${shot.capturedAt}-${i + 1}.png`, { type: mime });
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields. Category / Sub Issue Type are hidden on this form, so
    // they are not required here (the model defaults hlpd_ctgry_id when omitted).
    if (!ticketUdaId) {
      toastError('Please select the UDA this ticket belongs to', 2500);
      return;
    }
    if (
      !appId ||
      !componentId ||
      !ticketTypeId ||
      !mode ||
      !occurredTime ||
      !subject.trim() ||
      !description.trim()
    ) {
      toastError('Please fill in all required fields', 2500);
      return;
    }

    // One ticket = one MODULE. Screenshots may span different sub-modules of the same
    // module, but different MODULES must be raised as separate tickets.
    const distinctPaths = Array.from(new Set(screenshots.map((s) => toRoutePath(s.pageUrl)).filter(Boolean)));
    if (distinctPaths.length > 1) {
      try {
        const resolved = await Promise.all(distinctPaths.map((p) => helpdeskService.resolveMenuByPath(p)));
        const keys = new Set(
          resolved
            .map((r: any) => (r?.data as any)?.mdle_itm_id ?? null)
            .filter((v: any) => v != null)
            .map((v: any) => String(v))
        );
        if (keys.size > 1) {
          toastError('Your screenshots are from different modules. Please raise a separate ticket for each module.', 4000);
          return;
        }
      } catch (e) {
        console.error('Module consistency check failed', e); // don't hard-block on a resolve error
      }
    }

    setIsSubmitting(true);
    try {
      // 1) Upload attachments (captured screenshots + manual files) FIRST.
      const allFiles = [...screenshotsToFiles(), ...manualFiles];
      let uploaded: any[] = [];
      if (allFiles.length > 0) {
        try {
          uploaded = await helpdeskService.uploadAttachments(allFiles);
        } catch (uploadErr) {
          console.error('Attachment upload failed', uploadErr);
          toastError('Failed to upload attachments', 2500);
        }
      }

      // Page URL(s) where the issue occurred — captured with each screenshot, origin
      // stripped to the app route, de-duplicated.
      const pageUrls = Array.from(new Set(screenshots.map((s) => toRoutePath(s.pageUrl)).filter(Boolean)));
      // The explicit Path field (auto-filled from the first screenshot, editable) takes
      // precedence; fall back to the first captured route.
      const resolvedPath = toRoutePath(path.trim()) || pageUrls[0] || undefined;

      // Append the source route(s) into the description so they're visible in the ticket
      // body itself (not just the custom-fields JSON).
      const urlNote = pageUrls.length ? `\n\n---\nReported from: ${pageUrls.join('\n')}` : '';
      const finalDescription = `${description || ''}${urlNote}`.trim();

      // cstm_flds_jn is rendered as raw key/value in TicketDetail, so store the readable
      // menu-item names rather than the bare mnu_itm_id — same reason `mode` stores a name.
      const moduleLabel = components.find((m: any) => String(m.mnu_itm_id) === componentId)?.mnu_itm_nm;
      const subModuleLabel = subModules.find((s: any) => String(s.mnu_itm_id) === subModule)?.mnu_itm_nm;

      // 2) Create the ticket. The extra fields with no dedicated column ride
      //    along inside cstm_flds_jn (jsonb). Typed as `any` so the uploaded
      //    attachment metadata can sit on `attchmnt_jn`.
      const dataToSave: any = {
        sbj_tx: subject,
        dscn_tx: finalDescription,
        hlpd_app_id: Number(appId),
        // hlpd_cmpnt_id is FK-bound to hlpd_cmpnt_lst_t, which this form no longer uses.
        // Module / Sub Module are menu items, stored in their own columns.
        mnu_itm_id: componentId ? Number(componentId) : undefined,
        sub_mnu_itm_id: subModule ? Number(subModule) : undefined,
        hlpd_tckt_type_id: Number(ticketTypeId),
        // Category is hidden on this form — hlpd_ctgry_id is omitted so the model applies
        // its default (Category field commented out below).
        // hlpd_ctgry_id: categoryId ? Number(categoryId) : undefined,
        hlpd_prrty_id: priorityId ? Number(priorityId) : undefined,
        // Path + Reference Number are stored in their own columns (pge_path_tx / ref_no_tx),
        // in addition to the cstm_flds_jn mirror below (which the detail view renders).
        pge_path_tx: resolvedPath,
        ref_no_tx: refNo.trim() || undefined,
        cstm_flds_jn: {
          // Readable names only — the ids live in mnu_itm_id / sub_mnu_itm_id columns.
          module: moduleLabel || undefined,
          sub_module: subModuleLabel || undefined,
          // Sub Issue Type field hidden on this form (commented out below).
          // sub_issue_type: subIssueType || undefined,
          // cstm_flds_jn is rendered as raw key/value in TicketDetail, so store the
          // readable mode name rather than the bare cmpnt_id.
          mode: modes.find((m: any) => String(m.cmpnt_id) === mode)?.cmpnt_nm || mode || undefined,
          occurred_time: occurredTime || undefined,
          // Reference Number (Assessment No. / Water HSC Code / generic), optional.
          ref_no_tx: refNo.trim() || undefined,
          // The page route where the issue occurred (Path field / first screenshot).
          page_url: resolvedPath,
          page_urls: pageUrls.length > 0 ? pageUrls : undefined,
        },
        attchmnt_jn: uploaded.length > 0 ? uploaded : undefined,
        src_cd: 'WEB',
        // Declared in saveTicketCtrl's whitelist; resolveHelpdeskUdaId uses it for a
        // state user and keeps a UDA user on their own authority whatever is sent.
        uda_id: Number(ticketUdaId),
      };

      const saved = await helpdeskService.saveTicket(dataToSave);
      const savedData: any = saved?.data;
      const newTicketId = savedData?.hlpd_tckt_id || savedData?.data?.hlpd_tckt_id;

      if ((saved?.status === 200 || saved?.status === 201) && newTicketId) {
        success('Ticket submitted successfully!', 2000);
        clearScreenshots();
        bumpTicketsVersion();
        onCreated?.();
        closeModal();
      } else {
        toastError(saved?.message || 'Failed to submit ticket', 2500);
      }
    } catch (err: any) {
      console.error('Submit error', err, err?.response?.data);
      const raw =
        err?.response?.data?.message ||
        err?.response?.data?.errors?.[0]?.message ||
        err?.response?.data?.error ||
        err?.message || '';
      const looksLikeValidation = /required|invalid|parameter|attribute|allowed|valid|missing/i.test(raw);
      toastError(looksLikeValidation
        ? 'Please fill in all required fields correctly and try again.'
        : 'Could not submit the ticket. Please try again.', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const labelCls = 'block text-sm font-medium text-gray-700 mb-1.5';
  const inputCls = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
  const req = <span className="text-red-500">*</span>;

  return (
    <div className="p-4 sm:p-6">
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Screenshots span more than one module — one ticket may cover only one. */}
        {moduleConflict && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line text-base mt-0.5" />
            <span>
              Your screenshots are from <span className="font-semibold">different modules</span>.
              A ticket can cover only one module — please remove the extra screenshots and raise a separate ticket for each module.
            </span>
          </div>
        )}
        {/* Row 0 — UDA the ticket belongs to */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className={labelCls}>UDA {req}</label>
            {udaScope.canChoose ? (
              <select
                className={inputCls}
                value={ticketUdaId}
                disabled={udaScope.loading}
                onChange={(e) => setTicketUdaId(e.target.value)}
              >
                <option value="">{udaScope.loading ? 'Loading UDAs...' : 'Select UDA'}</option>
                {udaScope.udas.map((u) => (
                  <option key={u.uda_id} value={u.uda_id}>{u.uda_nm}</option>
                ))}
              </select>
            ) : (
              <div className={`${inputCls} bg-gray-50 text-gray-700`} title="Your UDA">
                {udaScope.label}
              </div>
            )}
          </div>
        </div>

        {/* Row 1 — Component / Module / Sub Modules / Issue Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Component */}
          <div>
            <label className={labelCls}>Component {req}</label>
            <select
              className={inputCls}
              value={appId}
              onChange={(e) => { setAppId(e.target.value); setComponentId(''); }}
            >
              <option value="">Select Component</option>
              {applications.map((a: any) => (
                <option key={a.hlpd_app_id} value={a.hlpd_app_id}>{a.hlpd_app_nm}</option>
              ))}
            </select>
          </div>

          {/* Module — root menu items (ds_dtcp.app_mnu_itm_lst_t, prnt_mnu_itm_id IS NULL) */}
          <div>
            <label className={labelCls}>Module {req}</label>
            <select
              className={inputCls}
              value={componentId}
              onChange={(e) => setComponentId(e.target.value)}
            >
              <option value="">
                {components.length === 0 ? 'No modules available' : 'Select Module'}
              </option>
              {components.map((m: any) => (
                <option key={m.mnu_itm_id} value={m.mnu_itm_id}>{m.mnu_itm_nm}</option>
              ))}
            </select>
          </div>

          {/* Sub Modules — children of the chosen Module (prnt_mnu_itm_id = mnu_itm_id) */}
          <div>
            <label className={labelCls}>Sub Modules</label>
            <select
              className={`${inputCls} disabled:bg-gray-100 disabled:cursor-not-allowed`}
              value={subModule}
              onChange={(e) => setSubModule(e.target.value)}
              disabled={!componentId}
            >
              <option value="">
                {!componentId ? 'Select a module first' : (subModules.length === 0 ? 'No sub modules available' : 'Select Sub Module')}
              </option>
              {subModules.map((s: any) => (
                <option key={s.mnu_itm_id} value={s.mnu_itm_id}>{s.mnu_itm_nm}</option>
              ))}
            </select>
          </div>

          {/* Issue Type */}
          <div>
            <label className={labelCls}>Issue Type {req}</label>
            <select className={inputCls} value={ticketTypeId} onChange={(e) => setTicketTypeId(e.target.value)}>
              <option value="">Select Issue Type</option>
              {ticketTypes.map((t: any) => (
                <option key={t.hlpd_tckt_type_id} value={t.hlpd_tckt_type_id}>{t.hlpd_tckt_type_nm}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2 — Mode / Occurred Time (Category & Sub Issue Type are hidden on this form) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Category field hidden on the New Ticket form (kept off to match the
              IT-Helpdesk ticket flow); hlpd_ctgry_id is defaulted by the model.
          <div>
            <label className={labelCls}>Category {req}</label>
            <select className={inputCls} value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">{categories.length === 0 ? 'No categories available' : 'Select Category'}</option>
              {categories.map((c) => (
                <option key={c.hlpd_ctgry_id} value={c.hlpd_ctgry_id}>{c.hlpd_ctgry_nm}</option>
              ))}
            </select>
          </div>
          */}

          {/* Sub Issue Type field hidden on the New Ticket form.
          <div>
            <label className={labelCls}>Sub Issue Type</label>
            <select
              className={inputCls}
              value={subIssueType}
              onChange={(e) => setSubIssueType(e.target.value)}
              disabled={!ticketTypeId}
            >
              <option value="">
                {!ticketTypeId ? 'Select an issue type first' : (subIssueTypes.length === 0 ? 'No sub issue types available' : 'Select Sub Issue Type')}
              </option>
              {subIssueTypes.map((s) => (
                <option key={s.hlpd_sub_issue_type_id} value={s.hlpd_sub_issue_type_id}>{s.hlpd_sub_issue_type_nm}</option>
              ))}
            </select>
          </div>
          */}

          {/* Mode ← getActiveModes() (ds_dtcp.app_cmpnt_lst_t) */}
          <div>
            <label className={labelCls}>Mode {req}</label>
            <select className={inputCls} value={mode} onChange={(e) => setMode(e.target.value)}>
              <option value="">{modes.length === 0 ? 'No modes available' : 'Select Mode'}</option>
              {modes.map((m: any) => (
                <option key={m.cmpnt_id} value={m.cmpnt_id}>{m.cmpnt_nm}</option>
              ))}
            </select>
          </div>

          {/* Occurred Time */}
          <div>
            <label className={labelCls}>Occurred Time {req}</label>
            <input
              type="datetime-local"
              className={inputCls}
              value={occurredTime}
              onChange={(e) => setOccurredTime(e.target.value)}
            />
          </div>

          {/* Reference Number (optional) — label adapts to the selected Module. */}
          <div>
            <label className={labelCls}>{refLabel}</label>
            <input
              type="text"
              className={inputCls}
              placeholder={refPlaceholder}
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
            />
          </div>
        </div>

        {/* Title */}
        <div>
          <div className="flex items-center justify-between">
            <label className={labelCls}>Title {req}</label>
            <span className={`text-xs ${subject.length >= 100 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
              {subject.length}/100
            </span>
          </div>
          <input
            type="text"
            className={inputCls}
            placeholder="Enter issue title (max 100 characters)"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={100}
          />
        </div>

        {/* Description */}
        <div>
          <div className="flex items-center justify-between">
            <label className={labelCls}>Description {req}</label>
            <span className={`text-xs ${description.length >= 5000 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
              {description.length}/5000
            </span>
          </div>
          <textarea
            className={`${inputCls} h-32 resize-none`}
            placeholder="Describe the issue in detail (max 5000 characters)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={5000}
          />
        </div>

        {/* Path — page route where the issue occurred. Auto-filled from the first
            screenshot; auto-resolves Module + Sub Module above. Editable. */}
        <div>
          <label className={labelCls}>Path</label>
          <input
            type="text"
            className={inputCls}
            placeholder="Page URL / route where the issue occurred"
            value={path}
            onChange={(e) => setPath(e.target.value)}
          />
          {/* <p className="text-xs text-gray-500 mt-1">Auto-filled from your screenshot; editing it re-detects the Module &amp; Sub Module.</p> */}
        </div>

        {/* Attachments */}
        <div>
          <label className={labelCls}>Attachments</label>
          <input
            type="file"
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 border border-gray-300 rounded-md cursor-pointer"
            multiple
            accept="image/*,.pdf,.doc,.docx"
            onChange={handleFiles}
          />
          <p className="text-xs text-gray-500 mt-1">Supported formats: Images, PDF, DOC, DOCX</p>

          {(screenshots.length > 0 || manualFiles.length > 0) && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Captured screenshots (from store) */}
              {screenshots.map((shot) => (
                <div key={shot.id} className="border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                  <div className="relative">
                    <img src={shot.dataUrl} alt="Screenshot" className="w-full h-24 object-cover object-top" />
                    <span className="absolute bottom-1 left-1 px-1 rounded bg-black/50 text-white text-[9px]">Screenshot</span>
                    <button
                      type="button"
                      onClick={() => removeScreenshot(shot.id)}
                      className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white text-xs hover:bg-red-600"
                      aria-label="Remove screenshot"
                    >
                      ×
                    </button>
                  </div>
                  {shot.pageUrl && (
                    <a
                      href={shot.pageUrl}
                      target="_blank"
                      rel="noreferrer"
                      title={shot.pageUrl}
                      className="block px-1.5 py-1 text-[10px] text-blue-600 truncate border-t border-gray-100 hover:underline"
                    >
                      {shot.pageUrl}
                    </a>
                  )}
                </div>
              ))}

              {/* Manually uploaded files */}
              {manualFiles.map((file, index) => {
                const isImage = file.type.startsWith('image/');
                const url = isImage ? URL.createObjectURL(file) : '';
                return (
                  <div key={index} className="relative border border-gray-200 rounded-md overflow-hidden bg-gray-50">
                    {isImage ? (
                      <img src={url} alt={file.name} className="w-full h-24 object-cover object-top" />
                    ) : (
                      <div className="w-full h-24 flex items-center justify-center text-xs text-gray-500 p-2 text-center">{file.name}</div>
                    )}
                    <button
                      type="button"
                      onClick={() => removeManual(index)}
                      className="absolute top-1 right-1 w-5 h-5 flex items-center justify-center rounded-full bg-black/50 text-white text-xs hover:bg-red-600"
                      aria-label="Remove attachment"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-5 border-t border-gray-200">
          <button
            type="button"
            onClick={closeModal}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting || moduleConflict}
            title={moduleConflict ? 'Screenshots span multiple modules — raise a separate ticket for each' : undefined}
            className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Issue'}
          </button>
        </div>
      </form>

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default ReportIssuePage;
